import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MapVersion } from '@ivy-monorepo/shared'
import Icon from '../../components/Icon'
import MapFilterCard from '../../components/admin/map/MapFilterCard'
import BottomVenueCards from '../../components/admin/map/BottomVenueCards'
import VenueDetailSheet from '../../components/admin/map/VenueDetailSheet'
import CoordinatesModal from '../../components/admin/map/CoordinatesModal'
import { flyTo } from '../../utils/liveMap/flyTo'
import { useVenuesNearby, LatLng } from '../../hooks/useVenuesNearby'
import { useSpotlightVenues, spotlightToNearby } from '../../hooks/useSpotlightVenues'
import { useMapSocket, VenuePatch } from '../../hooks/useMapSocket'
import { authService } from '../../services/auth.service'
import { MapFilters, MapFilterConfigItem, NearbyVenue } from '../../types/liveMap'
import { loadGoogleMaps } from '../../utils/googleMaps'
import { getHtmlMarkerClass, HtmlMarker } from '../../utils/liveMap/htmlMarker'
import { computePinCounts } from '../../utils/liveMap/computePinCounts'
import { buildMarkerContent, markerCacheKey, MarkerPulse } from '../../utils/liveMap/buildMarkerContent'
import { PULSE_CHECKIN_COLOR, PULSE_RSVP_COLOR } from '../../utils/liveMap/constants'
import {
  resolveFilterConfig,
  initialFiltersFromConfig,
  FALLBACK_MAP_FILTERS,
} from '../../utils/liveMap/filterConfig'

/**
 * Admin "Live Activity" map (/admin/live-map). Ports the mobile map: custom
 * badge markers, server-driven filter card, live pulses, bottom venue cards and
 * a venue detail sheet — all against the same backend (no BE changes).
 */

// Central Chicago (Near North / Division St area) — the nearby search box is only
// ±0.05° (~5.5km), so the default must sit near the venue cluster, not the Loop.
const FALLBACK_CENTER: LatLng = { lat: 41.9, lng: -87.64 }
const DEFAULT_ZOOM = 13
const SELECTED_ZOOM = 16
// Only refetch on pan once the center moves more than ~this many degrees (~150m).
const RECENTER_THRESHOLD_DEG = 0.0015

// Hide built-in business POIs so only our custom markers show (mirrors mobile).
const MAP_STYLES = [
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
]

interface VenueRender {
  counts: (number | null)[]
  pulse?: MarkerPulse
}

/** Computes the marker counts + pulse for a venue under the active filters. */
function computeVenueRender(venue: NearbyVenue, filters: MapFilters): VenueRender {
  const counts = computePinCounts(venue, filters)

  const redPulse = filters.checkIns && (venue.totalCheckins ?? 0) > 0
  const bluePulse =
    (filters.events && (venue.eventRsvpCount ?? 0) > 0) ||
    (filters.events && filters.visitPlans && (venue.visitPlanCount ?? 0) > 0)

  let pulse: MarkerPulse | undefined
  if (redPulse) pulse = { active: true, color: PULSE_CHECKIN_COLOR }
  else if (bluePulse) pulse = { active: true, color: PULSE_RSVP_COLOR }

  return { counts, pulse }
}

const LiveMapPage = () => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<any>(null)
  const htmlMarkerClassRef = useRef<{ new (opts: any): HtmlMarker } | null>(null)
  const markersRef = useRef<Map<string, { marker: HtmlMarker; key: string }>>(new Map())

  const [center, setCenter] = useState<LatLng | null>(FALLBACK_CENTER)
  const [mapReady, setMapReady] = useState(false)
  const [filterConfig, setFilterConfig] = useState<MapFilterConfigItem[]>(
    resolveFilterConfig(FALLBACK_MAP_FILTERS)
  )
  const [filters, setFilters] = useState<MapFilters>(() =>
    initialFiltersFromConfig(resolveFilterConfig(FALLBACK_MAP_FILTERS))
  )
  const [showFilters, setShowFilters] = useState(true)
  const [showCoordsModal, setShowCoordsModal] = useState(false)
  const [selectedVenue, setSelectedVenue] = useState<NearbyVenue | null>(null)
  // Brief "recomputing" flash when filters change (markers rebuild client-side).
  const [filterBusy, setFilterBusy] = useState(false)

  const { venues, setVenues, loading, error } = useVenuesNearby(center, MapVersion.New)
  const { spotlights } = useSpotlightVenues(center)

  // --- Load server filter config from /auth/me (once) ---
  const loadFilterConfig = useCallback(async () => {
    try {
      const me = await authService.getMe()
      if (!me?.mapFilters || me.mapFilters.length === 0) return
      const resolved = resolveFilterConfig(me.mapFilters as MapFilterConfigItem[])
      setFilterConfig(resolved)
      setFilters(initialFiltersFromConfig(resolved))
    } catch {
      /* fall back to the hardcoded config already in state */
    }
  }, [])

  useEffect(() => {
    loadFilterConfig()
  }, [loadFilterConfig])

  // --- Geolocate: default is Chicago; recenter only if location is granted ---
  useEffect(() => {
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      pos => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        setCenter(loc)
        const map = mapRef.current
        if (map) {
          // Map already initialized at the Chicago default — pan to the user.
          map.panTo(loc)
          map.setZoom(DEFAULT_ZOOM)
        }
      },
      () => {
        /* denied / unavailable — keep the Chicago default */
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }, [])

  // --- Initialize the map once (guard on mapRef so StrictMode's double-mount
  //     can't leave us without a map). Runs as soon as the container exists. ---
  useEffect(() => {
    if (mapRef.current || !center || !mapContainerRef.current) return

    let cancelled = false
    ;(async () => {
      await loadGoogleMaps()
      if (cancelled || mapRef.current || !mapContainerRef.current) return
      htmlMarkerClassRef.current = getHtmlMarkerClass()

      const map = new window.google.maps.Map(mapContainerRef.current, {
        center,
        zoom: DEFAULT_ZOOM,
        styles: MAP_STYLES,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        isFractionalZoomEnabled: true, // smoother zoom-out → pan → zoom-in fly
      })
      mapRef.current = map

      // Refetch venues when the user pans far enough.
      let idleTimeout: ReturnType<typeof setTimeout> | null = null
      map.addListener('idle', () => {
        if (idleTimeout) clearTimeout(idleTimeout)
        idleTimeout = setTimeout(() => {
          const c = map.getCenter()
          if (!c) return
          const next = { lat: c.lat(), lng: c.lng() }
          setCenter(prev => {
            if (
              prev &&
              Math.abs(prev.lat - next.lat) < RECENTER_THRESHOLD_DEG &&
              Math.abs(prev.lng - next.lng) < RECENTER_THRESHOLD_DEG
            ) {
              return prev
            }
            return next
          })
        }, 500)
      })

      setMapReady(true)
    })()

    return () => {
      cancelled = true
    }
  }, [center])

  // --- Fly to a venue (zoom out → pan → zoom in) and open its detail sheet ---
  const focusVenue = useCallback((venue: NearbyVenue) => {
    setSelectedVenue(venue)
    const map = mapRef.current
    if (map && venue.latitude && venue.longitude) {
      flyTo(map, { lat: venue.latitude, lng: venue.longitude }, SELECTED_ZOOM)
    }
  }, [])

  // --- Center the map on entered coordinates (with the same fly animation) ---
  const centerOnCoords = useCallback((lat: number, lng: number) => {
    const map = mapRef.current
    if (map) flyTo(map, { lat, lng }, SELECTED_ZOOM)
    // Update the query center so venues/spotlights refetch for the new area.
    setCenter({ lat, lng })
  }, [])

  // --- Sync markers whenever venues / filters change ---
  useEffect(() => {
    const HtmlMarkerCtor = htmlMarkerClassRef.current
    const map = mapRef.current
    if (!mapReady || !map || !HtmlMarkerCtor) return

    const markers = markersRef.current
    const seen = new Set<string>()

    for (const venue of venues) {
      seen.add(venue.publicId)
      const { counts, pulse } = computeVenueRender(venue, filters)
      const contentOpts = { name: venue.name, logoUrl: venue.logoUrl, counts, pulse }
      const key = markerCacheKey(contentOpts)
      const existing = markers.get(venue.publicId)

      if (existing && existing.key === key) continue // unchanged — keep cached

      const content = buildMarkerContent(contentOpts)
      const onClick = () => focusVenue(venue)

      if (existing) {
        existing.marker.setContent(content, onClick)
        existing.marker.setPosition({ lat: venue.latitude, lng: venue.longitude })
        existing.key = key
      } else {
        const marker = new HtmlMarkerCtor({
          map,
          position: { lat: venue.latitude, lng: venue.longitude },
          content,
          title: venue.name,
          onClick,
        })
        markers.set(venue.publicId, { marker, key })
      }
    }

    // Remove markers for venues that are no longer present.
    Array.from(markers.keys()).forEach(id => {
      if (!seen.has(id)) {
        const entry = markers.get(id)
        if (entry) entry.marker.setMap(null)
        markers.delete(id)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues, filters, mapReady])

  // Tear down all markers on unmount.
  useEffect(() => {
    const markers = markersRef.current
    return () => {
      markers.forEach(entry => {
        entry.marker.setMap(null)
      })
      markers.clear()
    }
  }, [])

  // --- Apply live socket patches to the in-memory venue list ---
  const handlePatch = useCallback(
    (patch: VenuePatch) => {
      if (patch.type === 'featureFlag') {
        // A map-related flag changed server-side; refresh the filter config.
        if (patch.key === 'mapFilters' || patch.key === 'mapVersion') loadFilterConfig()
        return
      }
      setVenues(prev =>
        prev.map(v => {
          if (v.publicId !== patch.venueId) return v
          switch (patch.type) {
            case 'checkin':
              return { ...v, totalCheckins: (v.totalCheckins ?? 0) + 1 }
            case 'checkout':
              return { ...v, totalCheckins: Math.max(0, (v.totalCheckins ?? 0) - 1) }
            default:
              return v
          }
        })
      )
    },
    [setVenues, loadFilterConfig]
  )

  // Subscribe the socket to the venues currently shown so we get their live activity.
  const venueIds = useMemo(() => venues.map(v => v.publicId), [venues])
  useMapSocket({ enabled: mapReady, onPatch: handlePatch, venueIds })

  const toggleFilter = useCallback((field: keyof MapFilters) => {
    setFilters(prev => ({ ...prev, [field]: !prev[field] }))
  }, [])

  // Flash the indicator briefly when filters change (markers recompute), and
  // keep it on while venues are loading. Skip the initial mount.
  const firstFilterRenderRef = useRef(true)
  useEffect(() => {
    if (firstFilterRenderRef.current) {
      firstFilterRenderRef.current = false
      return
    }
    setFilterBusy(true)
    const timeout = setTimeout(() => setFilterBusy(false), 350)
    return () => clearTimeout(timeout)
  }, [filters])

  const busy = loading || filterBusy

  // Keep the open detail sheet fresh from live venue data when available
  // (spotlight-selected venues may not be in the nearby set — fall back to it).
  const displayedVenue = useMemo(() => {
    if (!selectedVenue) return null
    return venues.find(v => v.publicId === selectedVenue.publicId) ?? selectedVenue
  }, [venues, selectedVenue])

  return (
    <div className="live-map">
      <div ref={mapContainerRef} className="live-map__canvas" />

      <button
        type="button"
        className="live-map__locate"
        title="Center on coordinates"
        aria-label="Center on coordinates"
        onClick={() => setShowCoordsModal(true)}
      >
        <Icon name="LocationCrosshairs" />
      </button>

      <CoordinatesModal
        isOpen={showCoordsModal}
        onClose={() => setShowCoordsModal(false)}
        onCenter={centerOnCoords}
        initialLat={center?.lat ?? null}
        initialLng={center?.lng ?? null}
      />

      <div className="map-overlay-top-left">
        {showFilters ? (
          <MapFilterCard
            config={filterConfig}
            filters={filters}
            onToggle={toggleFilter}
            onClose={() => setShowFilters(false)}
          />
        ) : (
          <button
            type="button"
            className="live-map__filter-toggle"
            onClick={() => setShowFilters(true)}
          >
            <Icon name="Sliders" /> Filters
          </button>
        )}

        {busy && (
          <div className="map-loading-indicator" role="status" aria-live="polite">
            <span className="map-loading-indicator__spinner" />
            <span>Loading…</span>
          </div>
        )}
      </div>

      {error && <div className="live-map__status live-map__status--error">{error}</div>}

      <BottomVenueCards
        venues={spotlights}
        filters={filters}
        activeVenueId={displayedVenue?.publicId ?? null}
        onSelect={s => focusVenue(spotlightToNearby(s))}
      />

      {displayedVenue && (
        <VenueDetailSheet venue={displayedVenue} onClose={() => setSelectedVenue(null)} />
      )}
    </div>
  )
}

export default LiveMapPage
